/**
 * Backup MFA codes display.
 *
 * Generates 10 one-time codes after TOTP setup and presents them to the
 * moderator. On acknowledgement the codes are POSTed to the
 * save-backup-codes Lambda, which bcrypt-hashes each one server-side
 * and stores the JSON array on the user's `custom:backup_codes_hash`
 * attribute. The verify-auth-challenge Cognito trigger then consumes
 * one entry per sign-in via the "Use a backup code" link on the TOTP
 * challenge screen.
 */

import { useEffect, useState } from 'react';
import { generateBackupCodes } from '../lib/auth';
import { saveBackupCodes } from '../lib/api';

interface BackupCodesProps {
  onAcknowledged: (codes: string[]) => void;
}

export default function BackupCodes({ onAcknowledged }: BackupCodesProps) {
  const [codes, setCodes] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setCodes(generateBackupCodes(10));
  }, []);

  const copyAll = async () => {
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        navigator.clipboard.writeText
      ) {
        await navigator.clipboard.writeText(codes.join('\n'));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // ignore; the codes are still visible on screen.
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await saveBackupCodes(codes);
      onAcknowledged(codes);
    } catch (err) {
      setSaveError(
        (err as Error).message ||
          'We could not save your backup codes. Try again, or copy them and continue.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="backup-codes" data-testid="backup-codes">
      <h2>Save your backup codes</h2>
      <p className="login-helper">
        Print or store these one-time codes in a safe place. Each code can be
        used once if you lose access to your authenticator app. We will not
        show them again.
      </p>
      <ol className="backup-codes__list" data-testid="backup-codes-list">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
      <div className="backup-codes__actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={copyAll}
          data-testid="backup-codes-copy"
        >
          {copied ? 'Copied' : 'Copy all'}
        </button>
      </div>
      <label className="cb-label cb-label--consent">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          data-testid="backup-codes-ack"
        />
        <span className="cb-text">
          <span className="cb-text__label">I have saved these codes.</span>
          <span className="cb-text__desc">
            We will hash and store these on your account so you can use one
            in place of a TOTP code if you ever lose your authenticator app.
          </span>
        </span>
      </label>
      {saveError && (
        <div className="field-error" role="alert" data-testid="backup-codes-save-error">
          {saveError}
        </div>
      )}
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleContinue}
        disabled={!confirmed || saving}
        data-testid="backup-codes-continue"
      >
        {saving ? 'Saving…' : 'Continue'}
      </button>
    </section>
  );
}
